/* eslint-env browser */
import React from 'react';
import PropTypes from 'prop-types';
import { isElementInViewport } from '../lib/helpers';
/**
 * AU Spinner Komponent
 * Bliver brugt til lazy-loading af elementer, der først skal indlæses når de er synlige.
 *
 * Eksempel:
 * ```jsx
 * <AUSpinnerComponent
          domID={spinnerID}
          visible={this.state.spinnerVisible}
          columns="large-12 medium-12 small-12"
          content={<h1>Hej</h1>}
          loaded={this.state.spinnerLoaded}
          loadingCondition
          onLoad={() => {
            setTimeout(() => {
              this.setState({
                spinnerLoaded: true,
              });
            }, 2000);
          }}
        />
 * ```
 */

class AUSpinnerComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      visible: props.visible,
    };

    this.lazyLoad = this.lazyLoad.bind(this);
  }

  componentDidMount() {
    this.lazyLoad();
    window.addEventListener('scroll', () => {
      this.lazyLoad();
    });
  }

  componentDidUpdate() {
    this.lazyLoad();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.visible !== prevState.visible) {
      return {
        visible: nextProps.visible,
      };
    }

    return null;
  }

  lazyLoad() {
    const {
      loadingCondition,
      loaded,
      domID,
      onLoad,
    } = this.props;
    const { loading, visible } = this.state;

    const element = document.getElementById(domID);
    if (!loaded && loading && loadingCondition && (visible || isElementInViewport(element))) {
      this.setState({
        loading: false,
      }, () => {
        onLoad();
      });
    }
  }

  render() {
    const {
      columns, content, loaded, domID, children,
    } = this.props;

    if (!loaded) {
      return (
        <div id={domID} className={`cell column ${columns}`}>
          <p className="spinner" />
        </div>
      );
    }

    return children || content;
  }
}

AUSpinnerComponent.defaultProps = {
  domID: 'au-spinner-component',
  visible: false,
  columns: 'large-12 medium-12 small-12',
  content: null,
  children: null,
  onLoad: () => {},
};

AUSpinnerComponent.propTypes = {
  /**
   * Relevant ved mere end en spinner per side
   */
  domID: PropTypes.string,
  /**
   * Om den er synlig på skærmen ved indlæsning
   */
  visible: PropTypes.bool,
  columns: PropTypes.string,
  content: PropTypes.element,
  children: PropTypes.element,
  loaded: PropTypes.bool.isRequired,
  loadingCondition: PropTypes.bool.isRequired,
  onLoad: PropTypes.func,
};

AUSpinnerComponent.displayName = 'AUSpinnerComponent';
export default AUSpinnerComponent;
